/**
 * imageDownscale.ts — the mandatory companion to camera capture.
 *
 * ── WHY THIS EXISTS AT ALL ────────────────────────────────────────────────
 * The company-documents step now offers `capture="environment"` on touch
 * devices, which turns "I'll send this later" into eight seconds of
 * photographing the certificate on the wall. A 12 MP phone photo of an A4
 * page routinely lands at 6–14 MB, and `POST /me/kyc/upload-url` is capped at
 * 10 MB — so without this file the headline feature fails on its FIRST real
 * use, on the exact device it was built for.
 *
 * 2400px on the long edge across A4 is ~200 DPI: comfortably legible for a
 * reviewer reading a registration number, and typically a 1–2 MB JPEG.
 *
 * ── THE RULES ─────────────────────────────────────────────────────────────
 * · Only touch images, and only over 4 MB. A 300 KB scan is left alone.
 * · Use the result ONLY if it is actually smaller. Re-encoding a small
 *   screenshot as JPEG can grow it.
 * · On ANY error — no canvas, a HEIC the browser cannot decode, a tainted
 *   context, an out-of-memory bitmap — return the ORIGINAL. Degrading to
 *   "your file is too big" would be worse than degrading to "unchanged": the
 *   size check downstream still runs, and `image/heic` is already accepted.
 * · Rename to `.jpg` so the signed-URL `ext` matches the JPEG contentType,
 *   or the reviewer downloads a file that claims to be a `.heic`.
 *
 * Zero dependencies. The pure helpers are exported so the arithmetic is
 * unit-testable without a canvas.
 */

/** Below this, a photo is small enough that re-encoding buys nothing. */
export const DOWNSCALE_OVER_BYTES = 4 * 1024 * 1024
/** Long-edge cap in CSS pixels. */
export const MAX_EDGE_PX = 2400
/** JPEG quality — visually lossless for text at this scale. */
export const JPEG_QUALITY = 0.85

/** Should we even try? Pure. */
export function shouldDownscale(file: { type?: string; size?: number }): boolean {
  const type = file.type ?? ''
  const size = file.size ?? 0
  return type.startsWith('image/') && size > DOWNSCALE_OVER_BYTES
}

/** Fit w×h inside a `max` long edge, preserving aspect. Pure. */
export function fitWithin(w: number, h: number, max = MAX_EDGE_PX): { width: number; height: number } {
  if (!Number.isFinite(w) || !Number.isFinite(h) || w <= 0 || h <= 0) return { width: 0, height: 0 }
  const longest = Math.max(w, h)
  if (longest <= max) return { width: Math.round(w), height: Math.round(h) }
  const scale = max / longest
  return { width: Math.max(1, Math.round(w * scale)), height: Math.max(1, Math.round(h * scale)) }
}

/** `gst-cert.heic` → `gst-cert.jpg`. Pure. */
export function jpegNameFor(name: string): string {
  const base = (name || '').replace(/\.[^./\\]+$/, '').trim()
  return `${base || 'document'}.jpg`
}

/** Decode to something `drawImage` accepts, without assuming `createImageBitmap`. */
async function decode(file: File): Promise<{
  source: CanvasImageSource; width: number; height: number; release: () => void
}> {
  if (typeof createImageBitmap === 'function') {
    const bitmap = await createImageBitmap(file)
    return { source: bitmap, width: bitmap.width, height: bitmap.height, release: () => bitmap.close?.() }
  }
  const url = URL.createObjectURL(file)
  try {
    const img = await new Promise<HTMLImageElement>((resolve, reject) => {
      const el = new Image()
      el.onload = () => resolve(el)
      el.onerror = () => reject(new Error('decode failed'))
      el.src = url
    })
    return {
      source: img,
      width: img.naturalWidth || img.width,
      height: img.naturalHeight || img.height,
      release: () => URL.revokeObjectURL(url),
    }
  } catch (err) {
    URL.revokeObjectURL(url)
    throw err
  }
}

/**
 * Downscale an oversized photo, or return the original untouched.
 *
 * NEVER throws — a failure here must not cost the user their upload.
 */
export async function downscaleImage(file: File): Promise<File> {
  if (!shouldDownscale(file)) return file
  let release = () => {}
  try {
    if (typeof document === 'undefined' || typeof HTMLCanvasElement === 'undefined') return file
    const decoded = await decode(file)
    release = decoded.release
    const { width, height } = fitWithin(decoded.width, decoded.height)
    if (!width || !height) return file

    const canvas = document.createElement('canvas')
    canvas.width = width
    canvas.height = height
    const ctx = canvas.getContext('2d')
    // jsdom (and any browser with canvas disabled) lands here — original kept.
    if (!ctx || typeof canvas.toBlob !== 'function') return file
    ctx.drawImage(decoded.source, 0, 0, width, height)

    const blob = await new Promise<Blob | null>((resolve) => {
      try { canvas.toBlob(resolve, 'image/jpeg', JPEG_QUALITY) } catch { resolve(null) }
    })
    if (!blob || blob.size >= file.size) return file
    return new File([blob], jpegNameFor(file.name), { type: 'image/jpeg', lastModified: Date.now() })
  } catch {
    return file
  } finally {
    try { release() } catch { /* nothing to release */ }
  }
}
